/**
 * Etapa 1 — Equipe, público e eletricidade (mockup 20 Estado A + calc legacy
 * IBIOMA pós-feedback).
 *
 * 2 q-blocks:
 *   - Equipe e público: 3 sliders (crew · capacity · audience_estimated)
 *   - Eletricidade: radio Sim/Não. Em "Sim", aparece:
 *       · Fonte (Rede elétrica / Gerador / Rede elétrica e Gerador)
 *       · Slider de consumo da rede (kWh ou BRL) quando source ∈ {grid, both}
 *       · Slider de combustível do gerador (L ou BRL) quando source ∈ {generator, both}
 *
 * Strings via bundle público de i18n.
 */
import type { EventosOrgInputs } from '../api/types';
import { t, type I18nBundle } from '../lib/i18n';
import { MetricWithUnitToggle } from './MetricWithUnitToggle';
import { NavRow, QBlock } from './QBlock';
import { Slider } from './Slider';

interface Props {
  value: EventosOrgInputs;
  onChange: (patch: Partial<EventosOrgInputs>) => void;
  onNext: () => void;
  i18n: I18nBundle;
}

export function StepOrgEquipePublico({ value, onChange, onNext, i18n }: Props) {
  const showGrid =
    value.electricity_source === 'grid' || value.electricity_source === 'both';
  const showGenerator =
    value.electricity_source === 'generator' || value.electricity_source === 'both';

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
      className="flex flex-col gap-4"
    >
      <QBlock title={t(i18n, 'org.step1.title')}>
        {/* As 3 perguntas já dizem "pessoas" no enunciado — omitimos o sufixo
          unit pra liberar espaço pro input numérico (eventos grandes têm
          5-6 dígitos: 50000+). */}
        <Slider
          label={t(i18n, 'org.step1.crew')}
          value={value.crew_count}
          onChange={(v) => onChange({ crew_count: v })}
          min={0}
          max={5000}
          step={1}
        />
        <Slider
          label={t(i18n, 'org.step1.capacity')}
          value={value.venue_capacity}
          onChange={(v) => onChange({ venue_capacity: v })}
          min={0}
          max={50000}
          step={10}
        />
        <Slider
          label={t(i18n, 'org.step1.audience')}
          value={value.audience_estimated}
          onChange={(v) => onChange({ audience_estimated: v })}
          min={0}
          max={50000}
          step={10}
        />
      </QBlock>

      <QBlock title={t(i18n, 'org.step1.electricity_title')}>
        <fieldset className="flex flex-col gap-3">
          <legend className="text-[14px] text-ink leading-snug mb-1">
            {t(i18n, 'org.step1.electricity_q')}
          </legend>
          <div className="flex gap-8 py-1">
            <RadioOption
              checked={value.electricity_paid === true}
              onClick={() =>
                onChange({
                  electricity_paid: true,
                  // Pré-seleciona "grid" quando user diz Sim — match com o
                  // mockup IBIOMA que já abre o card com Rede selecionada.
                  electricity_source: value.electricity_source ?? 'grid',
                })
              }
              label={t(i18n, 'org.common.yes')}
            />
            <RadioOption
              checked={value.electricity_paid === false}
              onClick={() =>
                onChange({
                  electricity_paid: false,
                  electricity_source: undefined,
                })
              }
              label={t(i18n, 'org.common.no')}
            />
          </div>
        </fieldset>

        {value.electricity_paid && (
          <div className="mt-5 pt-5 border-t border-dashed border-areia/60 flex flex-col gap-5">
            <fieldset>
              <legend className="text-[14px] text-ink leading-snug mb-3">
                {t(i18n, 'org.step1.electricity_source_q')}
              </legend>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SourceOption
                  checked={value.electricity_source === 'grid'}
                  onClick={() => onChange({ electricity_source: 'grid' })}
                  label={t(i18n, 'org.step1.source_grid')}
                />
                <SourceOption
                  checked={value.electricity_source === 'generator'}
                  onClick={() => onChange({ electricity_source: 'generator' })}
                  label={t(i18n, 'org.step1.source_generator')}
                />
                <SourceOption
                  checked={value.electricity_source === 'both'}
                  onClick={() => onChange({ electricity_source: 'both' })}
                  label={t(i18n, 'org.step1.source_both')}
                />
              </div>
            </fieldset>

            {showGrid && (
              <MetricWithUnitToggle
                label={t(i18n, 'org.step1.grid_consumption')}
                value={value.grid_consumption}
                units={['kWh', 'BRL']}
                onChange={(v) => onChange({ grid_consumption: v })}
              />
            )}

            {showGenerator && (
              <MetricWithUnitToggle
                label={t(i18n, 'org.step1.generator_fuel')}
                value={value.generator_fuel}
                units={['L', 'BRL']}
                onChange={(v) => onChange({ generator_fuel: v })}
              />
            )}
          </div>
        )}
      </QBlock>

      <NavRow nextIsSubmit nextLabel={t(i18n, 'org.common.next')} />
    </form>
  );
}

/** Radio circular custom — replica .radio-opt do mockup. */
function RadioOption({
  checked,
  onClick,
  label,
}: {
  checked: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className="inline-flex items-center gap-2.5 text-sm text-ink cursor-pointer hover:text-floresta transition-colors"
    >
      <span
        className={
          'inline-block size-5 rounded-full border-2 relative transition-colors ' +
          (checked ? 'border-floresta' : 'border-areia')
        }
      >
        {checked && <span aria-hidden className="absolute inset-[3px] rounded-full bg-floresta" />}
      </span>
      {label}
    </button>
  );
}

/** Radio em formato horizontal pra 3 opções de fonte de energia. */
function SourceOption({
  checked,
  onClick,
  label,
}: {
  checked: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      className="inline-flex items-center gap-2.5 text-sm text-ink cursor-pointer hover:text-floresta transition-colors text-left"
    >
      <span
        className={
          'inline-block size-5 rounded-full border-2 relative transition-colors shrink-0 ' +
          (checked ? 'border-floresta' : 'border-areia')
        }
      >
        {checked && <span aria-hidden className="absolute inset-[3px] rounded-full bg-floresta" />}
      </span>
      <span className="leading-snug">{label}</span>
    </button>
  );
}

